import RestUtilities from 'lib/RestUtilities';
import { AccountConfirmationResult, AccountLoginResult, AccountReconfirmationResult, AccountType } from 'models/ApiEnums';
import * as ApiModels from "models/ApiModels";
import { SaveUserRegisterDto } from 'models/ApiModels';
import Session from 'services/Session';

export interface AuthLanguages {
    culture: string;
    text: string;
    icon: string;
    locale: string;
}

export interface AuthenticationOptions {
    enableAutoRegister: boolean;
    enablePasswordRecovery: boolean;
    requireAccountConfirmation: boolean;

    policies: {
        maxInvalidPasswordAttempts: number;
        passwordAttemptWindow: number;
        passwordMinLength: number;
        passwordMinNumericChars: number;
        passwordMinLowerChars: number;
        passwordMinUpperChars: number;
        passwordMinSpecialChars: number;
    };
}

export interface ThemeModel {
    themeName: string;
    title: string;
    lastUpdateDate: Date;
}

export interface SessionSettingsModel {
    samlLoginUrl: string;
    samlLogoutUrl: string;
    authOptions: AuthenticationOptions;
    releaseNumber: string;
    languages: AuthLanguages[];
    theme: ThemeModel;
    defaultEmailAddress: string;
    tenantId: string;
}

export default class Auth {
    static isSignedIn(): boolean {
        return Session.cachedUser != null;
    }

    getAccountType(email: string): Promise<AccountType> {
        return RestUtilities.post("/api/v1.0/Session/GetAccountType", { authField: 'email', authValue: email });
    }

    updateLastLogin(): Promise<boolean> {
        return RestUtilities.post("/api/v1.0/Session/UpdateLastLogin", {}, true);
    }

    loginFromRefreshToken(refreshToken: string): Promise<ApiModels.AuthenticationResult> {
        return RestUtilities.post<ApiModels.AuthenticationResult>("/api/v1.0/Session/LoginFromRefreshToken", {
            refreshToken: refreshToken,
            clientTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        });
    }

    confirmAgreement(securityStamp: string, keepConnected: boolean): Promise<AccountLoginResult> {
        return RestUtilities.post<ApiModels.AuthenticationResult>("/api/v1.0/Session/ConfirmAgreement", {
            securityStamp,
            keepConnected: keepConnected,
            clientTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        })
            .then((response) => {
                if (response && response.webToken) {
                    Session.processNewSession(response.webToken);
                }
                return response.status;
            });
    }


    signIn(email: string, password: string, keepConnected: boolean): Promise<ApiModels.AuthenticationResult> {
        Session.clearSession();

        return RestUtilities.post<ApiModels.AuthenticationResult>("/api/v1.0/Session/Login",
            {
                authField: 'email',
                authValue: email,
                password: password,
                clientTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                keepConnected: keepConnected
            })
            .then((response) => {
                if (response && response.webToken) {
                    Session.processNewSession(response.webToken);
                }
                return response;
            });
    }

    signInWithSaml(samlResponse: string, keepConnected: boolean): Promise<ApiModels.AuthenticationResult> {
        Session.clearSession();

        return RestUtilities.post<ApiModels.AuthenticationResult>("/api/v1.0/Session/LoginWithSaml",
            {
                samlResponse,
                keepConnected: keepConnected,
                clientTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone
            })
            .then((response) => {
                if (response && response.webToken) {
                    Session.processNewSession(response.webToken);
                }
                return response;
            });
    }

    requestNewPassword(email: string): Promise<boolean> {
        return RestUtilities.post<boolean>('/api/v1.0/Session/RequestNewPassword',
            {
                authField: 'email',
                authValue: email
            })
            .then((response) => {
                return response;
            });
    }

    checkStampIsValid(securityStamp: string): Promise<boolean> {
        return RestUtilities.post<boolean>('/api/v1.0/Session/CheckStampIsValid',
            {
                securityStamp
            });
    }

    changePassword(securityStamp: string, newPassword: string): Promise<AccountLoginResult> {
        return RestUtilities.post<ApiModels.AuthenticationResult>('/api/v1.0/Session/ChangePassword',
            {
                securityStamp,
                newPassword,
                clientTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone
            })
            .then((response) => {
                if (response && response.webToken) {
                    Session.processNewSession(response.webToken);
                }
                return response.status;
            });
    }


    register(dto: SaveUserRegisterDto): Promise<AccountLoginResult> {

        Session.clearSession();

        return RestUtilities.post<ApiModels.AuthenticationResult>("/api/v1.0/Session/register", dto)
            .then((response) => {
                if (response && response.webToken) {
                    Session.processNewSession(response.webToken);
                }
                return response.status;
            });
    }

    requestNewAccountConfirmation(securityStamp: string): Promise<AccountReconfirmationResult> {
        return RestUtilities.post('/api/v1.0/Session/RequestNewAccountConfirmation', { securityStamp });
    }

    accountConfirmation(securityStamp: string): Promise<AccountConfirmationResult> {
        return RestUtilities.post('/api/v1.0/Session/AccountConfirmation', { securityStamp });
    }


    getSettings(): Promise<SessionSettingsModel> {
        return RestUtilities.get("/api/v1.0/Session/GetSettings", {}, true);
    }

    hasOneLicenseAvailable(): Promise<boolean> {
        return RestUtilities.get("/api/v1.0/Session/HasOneLicenseAvailable", {}, true);
    }

    resendMailConfirmation(authValue: string): Promise<boolean> {
        return RestUtilities.post<boolean>('/api/v1.0/Session/ResendConfirmationEmail',
            {
                authField: 'email',
                authValue: authValue
            })
            .then((response) => {
                return response;
            });
    }

    static signOut(corporateLogout: boolean = false): void {
        Session.clearSession();

        if (corporateLogout && Session.logoutOptions.samlLogoutUrl != null) {
            Auth.openSamlDialog(Session.logoutOptions.samlLogoutUrl);
        }

        RestUtilities.post<void>('/api/v1.0/Session/Logout', {});
    }

    ///
    static openSamlDialog(url: string) {
        var w = 600;
        var h = 450;
        var left = (screen.width / 2) - (w / 2);
        var top = (screen.height / 2) - (h / 2);

        window.open(url, "Genie External Authentication",
            `
            width=${w}, 
            height=${h}, 
            top=${top}, 
            left=${left},
            scrollbars=0,
            toolbar=0,
            location=0,
            menubar=0
            `
        );
    }
}
